import Accordion from "@/components/Accordion";
import { LanguageConfigModel, StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import React, { useEffect, useState } from "react";
import TextInput from "../../TextInput";
import { ChatWindow } from "../../types";
import useApi from "@/hooks/useApi";
import FeatureHint from "@/components/FeatureHint";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

interface ProviderCap {
  providerSlug: string;
  providerName: string;
  available: boolean;
  missingReason?: string;
  setupUrl?: string;
}

export function QuickTicket(props: Props) {
  const api = useApi();
  const style = props.workingChatWindow.styleConfig || {};
  const lang  = props.workingChatWindow.languageConfig || {};

  const [caps, setCaps] = useState<ProviderCap[] | null>(null);
  const [capsError, setCapsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.messaging.get("WidgetCapabilities/ticketing");
        if (cancelled) return;
        if (r.ok) setCaps(await r.json() as ProviderCap[]);
        else setCaps([]); // endpoint not deployed yet — graceful empty
      } catch (e) {
        if (!cancelled) {
          setCapsError((e as Error).message);
          setCaps([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const updateStyle = (u: Partial<StyleConfigModel>) =>
    props.setWorkingChatWindow({ ...props.workingChatWindow, styleConfig: { ...style, ...u } });
  const updateLang = (u: Partial<LanguageConfigModel>) =>
    props.setWorkingChatWindow({ ...props.workingChatWindow, languageConfig: { ...lang, ...u } });

  const available = (caps ?? []).filter(c => c.available);
  const unavailable = (caps ?? []).filter(c => !c.available);
  const activeSlug = style.quickTicketProviderSlug ?? available[0]?.providerSlug;

  return (
    <Accordion.Item eventKey="quick-ticket" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Quick Ticket</Accordion.Title>
        <Accordion.SubTitle>
          Let visitors submit a ticket that flows straight into your help desk
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="mb-2">
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium">Enable</div>
            <Toggle
              toggled={!!style.quickTicketEnabled}
              changeToggle={() => updateStyle({ quickTicketEnabled: !style.quickTicketEnabled })}
            />
          </div>

          {style.quickTicketEnabled && (
            <>
              <div className="my-3 border-t border-slate-200" />

              <div className="text-sm font-medium mb-2">Ticketing provider</div>
              {caps == null ? (
                <div className="text-xs text-slate-500">Checking connected ticketing integrations…</div>
              ) : available.length === 0 ? (
                <FeatureHint
                  hintKey="quick-ticket-no-provider"
                  severity="info"
                  title="No ticketing integration connected"
                  body={
                    unavailable.length > 0
                      ? `Connect ${unavailable.map(u => u.providerName).join(", ")} to enable Quick Ticket, or tickets will be stored in Velaro's native helpdesk only.`
                      : "Tickets will be stored in Velaro's native helpdesk. Connect ServiceNow, HubSpot, Zoho, or SAP to route them elsewhere."
                  }
                  cta={{ label: "Browse integrations →", to: "/Messaging/Integrations" }}
                />
              ) : (
                <select
                  aria-label="Ticketing provider"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                  value={activeSlug ?? ""}
                  onChange={e => updateStyle({ quickTicketProviderSlug: e.target.value || undefined })}
                >
                  {available.map(c => (
                    <option key={c.providerSlug} value={c.providerSlug}>{c.providerName}</option>
                  ))}
                </select>
              )}
              {capsError && (
                <div className="text-xs text-rose-700 mt-1">
                  Couldn't check integrations: {capsError}
                </div>
              )}

              <div className="my-3 border-t border-slate-200" />
              <div className="text-sm font-medium mb-2">Title</div>
              <TextInput
                value={lang.quickTicketTitle ?? ""}
                setValue={v => updateLang({ quickTicketTitle: v })}
                showEmojiButton={false}
              />
              <div className="text-sm font-medium my-2">Subtitle</div>
              <TextInput
                value={lang.quickTicketSubtitle ?? ""}
                setValue={v => updateLang({ quickTicketSubtitle: v })}
                showEmojiButton={false}
              />
              <div className="text-sm font-medium my-2">Submit button</div>
              <TextInput
                value={lang.quickTicketSubmitButton ?? ""}
                setValue={v => updateLang({ quickTicketSubmitButton: v })}
                showEmojiButton={false}
              />
              <div className="text-sm font-medium my-2">Success message</div>
              <TextInput
                value={lang.quickTicketSuccessMessage ?? ""}
                setValue={v => updateLang({ quickTicketSuccessMessage: v })}
                showEmojiButton={false}
              />
            </>
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
